import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import DB from '../../../lib/db';
import Modal from '../../../components/modal';
import Button from '../../../components/button';
import SvgSuccessMark from '../../../assets/svg/success-mark.svg';
import DailyReportModalSC from './daily-report-modal.styled';
import { useParams } from 'react-router-dom';
import { memberApi, memberApiV2 } from '../../../lib/api';
import { TDailyReportData } from '../../../types/vital-data';
import { TCurrentUser } from '../../../types/users';
import { useSelector } from 'react-redux';
import TStore from '../../../types/store';
import { TPatientDetail } from '../../../types/patients';

type TDailyReportModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
};

const DAILY_REPORT_MODAL_FIRST_STEP = 'FIRST';
const DAILY_REPORT_MODAL_SECOND_STEP = 'SECOND';

type TDailyReportModalStep =
  | typeof DAILY_REPORT_MODAL_FIRST_STEP
  | typeof DAILY_REPORT_MODAL_SECOND_STEP;

type TDailyReportModalFirstStepProps = {
  handleNextStepBtnClick: () => void;
};

const createDateTimeString = (date: string): string =>
  `[${moment(date).format('DD.MM.YYYY HH:mm:ss')}]: `;

/**
 * TODO:
 * - Nova sprava sa neulozi do cache
 *
 * @param handleNextStepBtnClick
 * @param handleDatePickerBtnClick
 * @constructor
 */
const DailyReportModalFirstStep: React.FC<TDailyReportModalFirstStepProps> = ({
  handleNextStepBtnClick,
}: TDailyReportModalFirstStepProps) => {
  const doctor: TCurrentUser | null = useSelector((store: TStore) => store.users.currentUser);
  const patient: TPatientDetail | null = useSelector(
    (store: TStore) => store.patients.patientDetail,
  );
  const providerId = useSelector((state: TStore) => state.users.business[0].id);

  const patientsName = `${(patient as TPatientDetail).profile.firstName} ${
    (patient as TPatientDetail).profile.lastName
  }`;
  const doctorsName = `${(doctor as TCurrentUser).profile.firstName} ${
    (doctor as TCurrentUser).profile.lastName
  }`;

  const params = useParams<{ jabberId: string; userId: string }>();
  const intl = useIntl();
  const textAreaRef = useRef(null);
  const [dateWidgetOpen, setDateWidgetOpen] = useState(false);
  const [date, setDate] = useState<Date>(new Date());
  const [chatData, setChatData] = useState<any>(null);
  const [vitalData, setVitalData] = useState<TDailyReportData | null>(null);

  const dataRenderer = (vitalData: TDailyReportData, chatMessages: any): string => {
    let result = '';
    // process vital data
    if (vitalData !== null) {
      if (vitalData.bloodPressureRecords.length) {
        result += intl.formatMessage({ id: 'life_id.bloodPressure' }) + '\n';
        for (const record of vitalData.bloodPressureRecords) {
          result += createDateTimeString(record.datetime);
          result += `${record.systolic}/${record.diastolic}`;
          result += '\n';
        }
        result += '\n';
      }

      if (vitalData.bodyMeasureRecords.length) {
        result += intl.formatMessage({ id: 'life_id.bmi_index' }) + '\n';
        for (const record of vitalData.bodyMeasureRecords) {
          result += createDateTimeString(record.datetime);
          result += `${intl.formatMessage({ id: 'life_id.height' })}: ${
            record.height
          }, ${intl.formatMessage({
            id: 'life_id.weight',
          })}: ${record.weight}, ${intl.formatMessage({
            id: 'life_id.daily_report_bm_bmi',
          })}: ${record.bmi}`;
          result += '\n';
        }
        result += '\n';
      }

      if (vitalData.bodyTemperatureRecords.length) {
        result += intl.formatMessage({ id: 'life_id.temperature' }) + '\n';
        for (const record of vitalData.bodyTemperatureRecords) {
          result += `[${moment(record.datetime).format('DD.MM.YYYY HH:mm:ss')}]: `;
          result += record.temperature;
          result += '\n';
        }
        result += '\n';
      }

      if (vitalData.breathRate.length) {
        result += intl.formatMessage({ id: 'life_id.breath_frequency' }) + '\n';
        for (const record of vitalData.breathRate) {
          result += `[${moment(record.datetime).format('DD.MM.YYYY HH:mm:ss')}]: `;
          result += record.breathRate;
          result += '\n';
        }
        result += '\n';
      }

      if (vitalData.cholesterolRecords.length) {
        result += intl.formatMessage({ id: 'life_id.cholesterol' }) + '\n';
        for (const record of vitalData.cholesterolRecords) {
          result += `[${moment(record.datetime).format('DD.MM.YYYY HH:mm:ss')}]: `;
          result += record.cholesterol;
          result += '\n';
        }
        result += '\n';
      }

      if (vitalData.glucoseRecords.length) {
        result += intl.formatMessage({ id: 'life_id.glucose' }) + '\n';
        for (const record of vitalData.glucoseRecords) {
          result += `[${moment(record.datetime).format('DD.MM.YYYY HH:mm:ss')}]: `;
          result += record.glucose;
          result += '\n';
        }
        result += '\n';
      }

      if (vitalData.hearthRateRecords.length) {
        result += intl.formatMessage({ id: 'life_id.heartRate' }) + '\n';
        for (const record of vitalData.hearthRateRecords) {
          result += `[${moment(record.datetime).format('DD.MM.YYYY HH:mm:ss')}]: `;
          result += record.bpm;
          result += '\n';
        }
        result += '\n';
      }

      if (vitalData.oxygenSaturationRecords.length) {
        result += intl.formatMessage({ id: 'life_id.saturation' }) + '\n';
        for (const record of vitalData.oxygenSaturationRecords) {
          result += `[${moment(record.datetime).format('DD.MM.YYYY HH:mm:ss')}]: `;
          result += record.oxygenSaturation;
          result += '\n';
        }
        result += '\n';
      }
    }

    // process chat messages
    if (chatData !== null && chatMessages.length) {
      result += intl.formatMessage({ id: 'chat_tabs.chat' }) + '\n';
      const reversed = [...chatMessages].reverse();
      for (const record of reversed) {
        result += `[${moment(record.stamp).format('DD.MM.YYYY HH:mm:ss')} - `;
        result += `${record.from === params.jabberId ? doctorsName : patientsName}]: `;

        if (record.attachment) {
          result += `[${intl.formatMessage({ id: 'life_id.daily_report_attachment' })}]`;
          if (record.body !== 'attachment') {
            result += `: ${record.body}`;
          }
        } else {
          result += record.body;
        }

        result += '\n';
      }
      result += '\n';
    }

    return result;
  };

  useEffect(() => {
    const getVitalData = async () => {
      const dailyReportData = (
        await memberApiV2.getDailyReport(providerId, params.userId, new Date(date).toISOString())
      ).data;
      setVitalData(dailyReportData.data);
    };
    getVitalData();
  }, [date, params.userId]);

  useEffect(() => {
    const getMessages = async () => {
      const result = await DB.getDailyMessages(params.jabberId, date);
      await setChatData(result);
    };
    getMessages();
  }, [date, params.jabberId]);

  return (
    <div className="drm-first-step">
      <span className="drm-first-step__heading">
        {intl.formatMessage(
          {
            id: 'life_id.drm_widget_fs_heading',
          },
          { patient: patientsName },
        )}
      </span>
      <div className="drm-first-step__date">
        <span>{intl.formatMessage({ id: 'life_id.drm_widget_fs_date_label' })}</span>
        <div className="date_wrapper">
          <DatePicker
            open={dateWidgetOpen}
            selected={date}
            dateFormat="dd.MM.yyyy"
            onInputClick={() => {
              setDateWidgetOpen(true);
            }}
            onChange={(date) => {
              setDate(date as Date);
              setDateWidgetOpen(false);
            }}
            onClickOutside={() => {
              setDateWidgetOpen(true);
            }}
          />
        </div>
      </div>
      <div className="drm-first-step__data">
        <textarea
          ref={textAreaRef}
          value={dataRenderer(vitalData as TDailyReportData, chatData)}
          onChange={() => null}
        />
      </div>
      <Button
        kind="PRIMARY"
        size="MD"
        onClick={() => {
          if (textAreaRef.current !== null) {
            // @ts-ignore
            textAreaRef.current.select();
            document.execCommand('copy');
          }
          handleNextStepBtnClick();
        }}
        isBlock
        className="drm-next-step-btn"
        text={intl.formatMessage({ id: 'life_id.drm_widget_fs_copy_btn_text' })}
      />
    </div>
  );
};

const DailyReportModalSecondStep: React.FC = () => {
  const intl = useIntl();
  return (
    <div className="drm-second-step">
      <div className="drm-second-step__left">
        <img src={SvgSuccessMark} alt="success_icon" />
      </div>
      <div className="drm-second-step__right">
        <span>{intl.formatMessage({ id: 'life_id.drm_widget_ss_heading' })}</span>
        <span>{intl.formatMessage({ id: 'life_id.drm_widget_ss_subheading' })}</span>
      </div>
    </div>
  );
};

const DailyReportModal: React.FC<TDailyReportModalProps> = ({
  isOpen,
  onRequestClose,
}: TDailyReportModalProps) => {
  const [step, setStep] = useState<TDailyReportModalStep>(DAILY_REPORT_MODAL_FIRST_STEP);

  const renderStep = () => {
    if (step === DAILY_REPORT_MODAL_FIRST_STEP) {
      return (
        <DailyReportModalFirstStep
          handleNextStepBtnClick={() => setStep(DAILY_REPORT_MODAL_SECOND_STEP)}
        />
      );
    }
    return <DailyReportModalSecondStep />;
  };

  const handleCloseModalBtn = () => {
    setStep(DAILY_REPORT_MODAL_FIRST_STEP);
    onRequestClose();
  };

  return (
    <Modal isOpen={isOpen} showClose onRequestClose={handleCloseModalBtn}>
      <DailyReportModalSC>{renderStep()}</DailyReportModalSC>
    </Modal>
  );
};

export default DailyReportModal;
